import React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Layout from '../../components/Layout';
import { doGET } from '../../utils/HttpUtils';
import { downloadCsvFile } from '../../utils/export';
import { fetchInvoices } from '../../services/invoices';
import { api } from '../../services/api/apiHandler';
import { ICorporate } from '../../types/corporates';
import { InvoiceFilters, InvoicePaymentSource, InvoiceRow, InvoiceStatus, InvoiceSummary } from '../../types/invoices';

const defaultFilters: InvoiceFilters = { status: 'paid' };
const emptySummary: InvoiceSummary = { baseAmount: 0, discountAmount: 0, walletAmount: 0, finalAmount: 0 };
const INDIA_TIME_ZONE = 'Asia/Kolkata';

const titleCase = (value?: string) => value
    ? value.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase())
    : '—';

const formatCurrency = (amount = 0, currency = 'INR') =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);

const formatDateTime = (value?: string) => value
    ? new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: INDIA_TIME_ZONE })
    : '—';

const resolveDocumentUrl = (value?: string) => {
    if (!value) return '';
    try {
        return new URL(value, api.defaults.baseURL).toString();
    } catch (_error) {
        return value;
    }
};

const statusColor = (status: InvoiceStatus) => {
    if (status === 'paid') return 'success' as const;
    if (status === 'failed') return 'error' as const;
    if (status === 'refunded') return 'info' as const;
    return 'warning' as const;
};

const fetchAllPages = async (path: string) => {
    const rows: any[] = [];
    let page = 1;
    let totalPages = 1;

    do {
        const response = await doGET(`${path}${path.includes('?') ? '&' : '?'}page=${page}&limit=100`);
        if (!(response?.status >= 200 && response.status < 400)) {
            throw new Error(`Failed to load ${path.slice(1)} filters`);
        }
        const pageRows = response.data?.data?.rows || response.data?.data;
        if (Array.isArray(pageRows)) rows.push(...pageRows);
        totalPages = Number(response.data?.pagination?.totalPages || 1);
        page += 1;
    } while (page <= totalPages);

    return rows;
};

const Invoices = () => {
    const [draftFilters, setDraftFilters] = React.useState<InvoiceFilters>(defaultFilters);
    const [filters, setFilters] = React.useState<InvoiceFilters>(defaultFilters);
    const [rows, setRows] = React.useState<InvoiceRow[]>([]);
    const [summary, setSummary] = React.useState<InvoiceSummary>(emptySummary);
    const [corporates, setCorporates] = React.useState<ICorporate[]>([]);
    const [couponCodes, setCouponCodes] = React.useState<string[]>([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(20);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [exporting, setExporting] = React.useState(false);
    const [error, setError] = React.useState('');
    const [filterError, setFilterError] = React.useState('');

    const loadInvoices = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const result = await fetchInvoices(filters, page + 1, rowsPerPage);
            setRows(result.rows);
            setTotal(result.total);
            setSummary(result.summary);
        } catch (loadError) {
            setRows([]);
            setTotal(0);
            setSummary(emptySummary);
            setError((loadError as Error).message);
        } finally {
            setLoading(false);
        }
    }, [filters, page, rowsPerPage]);

    React.useEffect(() => { loadInvoices(); }, [loadInvoices]);

    React.useEffect(() => {
        let active = true;
        Promise.all([
            fetchAllPages('/corporates'),
            fetchAllPages('/coupons'),
        ]).then(([corporateRows, couponRows]) => {
            if (!active) return;
            setCorporates(Array.isArray(corporateRows) ? corporateRows : []);
            setCouponCodes(
                Array.from(new Set(
                    (Array.isArray(couponRows) ? couponRows : [])
                        .map((coupon: any) => coupon.code)
                        .filter(Boolean)
                )).sort() as string[]
            );
        }).catch((loadError) => {
            if (active) setFilterError((loadError as Error).message);
        });
        return () => { active = false; };
    }, []);

    const updateDraft = <K extends keyof InvoiceFilters>(key: K, value: InvoiceFilters[K]) =>
        setDraftFilters(current => ({ ...current, [key]: value || undefined }));

    const applyFilters = (event: React.FormEvent) => {
        event.preventDefault();
        setPage(0);
        setFilters({ ...draftFilters, search: draftFilters.search?.trim() || undefined });
    };

    const clearFilters = () => {
        setDraftFilters(defaultFilters);
        setFilters(defaultFilters);
        setPage(0);
    };

    const exportInvoices = async () => {
        setExporting(true);
        setError('');
        try {
            const result = await fetchInvoices(filters, 1, rowsPerPage, true);
            if (result.total > result.rows.length) {
                throw new Error(
                    `Export matches ${result.total.toLocaleString('en-IN')} transactions. Narrow the filters to export at most ${result.rows.length.toLocaleString('en-IN')} at a time.`
                );
            }
            downloadCsvFile(
                `invoices-${new Intl.DateTimeFormat('en-CA', { timeZone: INDIA_TIME_ZONE }).format(new Date())}.csv`,
                ['Payment ID', 'Payment Date', 'User', 'Email', 'Corporate', 'Booking Type', 'Booking Reference', 'Booking Date', 'Item', 'Payment Source', 'Coupon', 'Gross Amount', 'Discount', 'Wallet', 'Razorpay / Payable', 'Status', 'Razorpay Payment ID', 'Document Number', 'Document Status', 'Document URL'],
                result.rows.map(row => [
                    row.id,
                    formatDateTime(row.paidAt || row.createdAt),
                    row.userName || '',
                    row.userEmail || '',
                    row.corporateName || '',
                    titleCase(row.context),
                    row.bookingReference,
                    row.bookingDate || '',
                    row.itemName || '',
                    titleCase(row.paymentSource),
                    row.couponCode || '',
                    row.baseAmount,
                    row.discountAmount,
                    row.walletAmount,
                    row.finalAmount,
                    titleCase(row.status),
                    row.razorpayPaymentId || '',
                    row.document?.number || '',
                    row.document ? titleCase(row.document.status) : '',
                    resolveDocumentUrl(row.document?.url),
                ])
            );
        } catch (exportError) {
            setError((exportError as Error).message);
        } finally {
            setExporting(false);
        }
    };

    return (
        <Layout appBarTitle="Invoices">
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>Invoices</Typography>
                    <Typography color="text.secondary">
                        Review booking payments, funding splits, coupons, and generated billing documents.
                    </Typography>
                </Box>

                <Paper component="form" onSubmit={applyFilters} variant="outlined" sx={{ p: 2.5 }}>
                    <Stack spacing={2}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Filter transactions</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
                            <TextField size="small" label="Search" placeholder="User, booking, coupon or payment ID" value={draftFilters.search || ''} onChange={event => updateDraft('search', event.target.value)} />
                            <FormControl size="small">
                                <InputLabel>Corporate</InputLabel>
                                <Select label="Corporate" value={draftFilters.corporateId || ''} onChange={event => updateDraft('corporateId', event.target.value)}>
                                    <MenuItem value="">All corporates</MenuItem>
                                    {corporates.map(corporate => <MenuItem key={corporate._id} value={corporate._id}>{corporate.companyName || corporate.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl size="small">
                                <InputLabel>Payment source</InputLabel>
                                <Select label="Payment source" value={draftFilters.paymentSource || ''} onChange={event => updateDraft('paymentSource', event.target.value as InvoicePaymentSource)}>
                                    <MenuItem value="">All sources</MenuItem>
                                    {['coupon', 'razorpay', 'mixed', 'wallet', 'internal'].map(source => <MenuItem key={source} value={source}>{titleCase(source)}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl size="small">
                                <InputLabel>Coupon</InputLabel>
                                <Select label="Coupon" value={draftFilters.couponCode || ''} onChange={event => updateDraft('couponCode', event.target.value)}>
                                    <MenuItem value="">All coupons</MenuItem>
                                    {couponCodes.map(code => <MenuItem key={code} value={code}>{code}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl size="small">
                                <InputLabel>Payment status</InputLabel>
                                <Select label="Payment status" value={draftFilters.status || ''} onChange={event => updateDraft('status', event.target.value as InvoiceStatus)}>
                                    <MenuItem value="">All statuses</MenuItem>
                                    {['created', 'paid', 'failed', 'refunded'].map(status => <MenuItem key={status} value={status}>{titleCase(status)}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl size="small">
                                <InputLabel>Booking type</InputLabel>
                                <Select label="Booking type" value={draftFilters.context || ''} onChange={event => updateDraft('context', event.target.value as InvoiceFilters['context'])}>
                                    <MenuItem value="">All booking types</MenuItem>
                                    <MenuItem value="booking">Consultation / wellness</MenuItem>
                                    <MenuItem value="external_booking">External booking</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField size="small" type="date" label="From date" InputLabelProps={{ shrink: true }} value={draftFilters.fromDate || ''} onChange={event => updateDraft('fromDate', event.target.value)} />
                            <TextField size="small" type="date" label="To date" InputLabelProps={{ shrink: true }} value={draftFilters.toDate || ''} onChange={event => updateDraft('toDate', event.target.value)} />
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                            <Button type="button" startIcon={<RestartAltIcon />} onClick={clearFilters}>Reset</Button>
                            <Button type="submit" variant="contained">Apply filters</Button>
                            <Button type="button" variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : <DownloadOutlinedIcon />} onClick={exportInvoices} disabled={exporting || total === 0}>
                                Export CSV
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                {error ? <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadInvoices}>Retry</Button>}>{error}</Alert> : null}
                {filterError ? <Alert severity="warning">{filterError}. The invoice list is still available.</Alert> : null}

                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} divider={undefined} sx={{ px: 2.5, py: 2, gap: { xs: 1.5, md: 4 }, borderBottom: 1, borderColor: 'divider' }}>
                        <Box><Typography variant="caption" color="text.secondary">Transactions</Typography><Typography sx={{ fontWeight: 700 }}>{total.toLocaleString('en-IN')}</Typography></Box>
                        <Box><Typography variant="caption" color="text.secondary">Gross</Typography><Typography sx={{ fontWeight: 700 }}>{formatCurrency(summary.baseAmount)}</Typography></Box>
                        <Box><Typography variant="caption" color="text.secondary">Discount</Typography><Typography sx={{ fontWeight: 700 }}>{formatCurrency(summary.discountAmount)}</Typography></Box>
                        <Box><Typography variant="caption" color="text.secondary">Wallet</Typography><Typography sx={{ fontWeight: 700 }}>{formatCurrency(summary.walletAmount)}</Typography></Box>
                        <Box><Typography variant="caption" color="text.secondary">Gateway amount</Typography><Typography sx={{ fontWeight: 700 }}>{formatCurrency(summary.finalAmount)}</Typography></Box>
                    </Stack>

                    {loading ? (
                        <Box role="status" sx={{ display: 'grid', placeItems: 'center', minHeight: 280 }}><CircularProgress aria-label="Loading invoices" /></Box>
                    ) : rows.length === 0 ? (
                        <Box sx={{ px: 3, py: 8, textAlign: 'center' }}>
                            <Typography variant="h6">No invoices match these filters</Typography>
                            <Typography color="text.secondary">Try a wider date range or reset one of the payment filters.</Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="small" sx={{ minWidth: 1180 }} aria-label="Invoice transactions">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Payment date</TableCell>
                                        <TableCell>User / corporate</TableCell>
                                        <TableCell>Booking</TableCell>
                                        <TableCell>Payment source</TableCell>
                                        <TableCell align="right">Gross</TableCell>
                                        <TableCell align="right">Discount</TableCell>
                                        <TableCell align="right">Wallet</TableCell>
                                        <TableCell align="right">Gateway</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Document</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map(row => (
                                        <TableRow key={row.id} hover>
                                            <TableCell>
                                                <Typography variant="body2">{formatDateTime(row.paidAt || row.createdAt)}</Typography>
                                                <Typography variant="caption" color="text.secondary">{row.id}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.userName || 'Unknown user'}</Typography>
                                                <Typography variant="caption" display="block" color="text.secondary">{row.userEmail || '—'}</Typography>
                                                <Typography variant="caption" color="text.secondary">{row.corporateName || 'No corporate'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.itemName || titleCase(row.context)}</Typography>
                                                <Typography variant="caption" display="block" color="text.secondary">{row.bookingReference}</Typography>
                                                <Typography variant="caption" color="text.secondary">{row.bookingDate || '—'} {row.bookingTime || ''}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" label={titleCase(row.paymentSource)} variant="outlined" />
                                                {row.couponCode ? <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{row.couponCode}</Typography> : null}
                                            </TableCell>
                                            <TableCell align="right">{formatCurrency(row.baseAmount, row.currency)}</TableCell>
                                            <TableCell align="right">{formatCurrency(row.discountAmount, row.currency)}</TableCell>
                                            <TableCell align="right">{formatCurrency(row.walletAmount, row.currency)}</TableCell>
                                            <TableCell align="right">{formatCurrency(row.finalAmount, row.currency)}</TableCell>
                                            <TableCell><Chip size="small" color={statusColor(row.status)} label={titleCase(row.status)} /></TableCell>
                                            <TableCell>
                                                {row.document ? (
                                                    <Button size="small" endIcon={<OpenInNewIcon />} href={resolveDocumentUrl(row.document.url)} target="_blank" rel="noreferrer">
                                                        {row.document.status === 'CANCELLED' ? 'Cancelled ' : ''}{row.document.kind === 'FINAL_INVOICE' ? 'Invoice' : 'Receipt'}
                                                    </Button>
                                                ) : <Typography variant="caption" color="text.secondary">Not generated</Typography>}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        onPageChange={(_, nextPage) => setPage(nextPage)}
                        onRowsPerPageChange={event => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
                    />
                </Paper>
            </Stack>
        </Layout>
    );
};

export default Invoices;
